export interface UndMedidaOption {
  id: number;
  nome: string;
}

function isPesoSomenteRotuloUnd(valor: string): boolean {
  const norm = valor
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
  return /^(und|unid|unidade|unidades)$/.test(norm);
}

export function formatPesoPadraoComUnd(
  pesoPadrao: string | null | undefined,
  undMedidaId: number | "" | null | undefined,
  unds: UndMedidaOption[]
): string {
  const v = (pesoPadrao ?? "").trim();
  const u = undNomeById(undMedidaId, unds);
  if (isPesoSomenteRotuloUnd(v)) {
    return u || v;
  }
  return formatQuantidadeComUnd(pesoPadrao, undMedidaId, unds);
}

export function formatQuantidadeComUnd(
  valor: string | null | undefined,
  undMedidaId: number | "" | null | undefined,
  unds: UndMedidaOption[]
): string {
  const v = (valor ?? "").trim();
  const undId = undMedidaId === "" ? undefined : undMedidaId;
  const und = unds.find((u) => u.id === undId);
  const u = und?.nome?.trim() ?? "";
  if (!v) return "";
  if (!u) return v;
  return `${v} ${u}`;
}

export function undNomeById(
  undMedidaId: number | "" | null | undefined,
  unds: UndMedidaOption[]
): string {
  if (undMedidaId === "" || undMedidaId == null || undMedidaId < 1) return "";
  return unds.find((u) => u.id === undMedidaId)?.nome ?? "";
}

/** UND de sobra/produção na ficha: usa a escolhida ou sugere a da base. */
export function resolveUndFicha(
  undMedidaIdBase: number | null | undefined,
  salva: number | "" | null | undefined
): number | "" {
  if (salva !== "" && salva != null && salva >= 1) return salva;
  if (undMedidaIdBase != null && undMedidaIdBase >= 1) return undMedidaIdBase;
  return "";
}

type UndMassa = "gr" | "kg";

function parseQuantidade(valor: string | null | undefined): number | null {
  const n = Number.parseFloat((valor ?? "").trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function normalizarUndMassa(nome: string | null | undefined): UndMassa | null {
  const norm = (nome ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
  if (norm === "gr" || norm === "g" || norm.startsWith("gram")) return "gr";
  if (norm === "kg" || norm.startsWith("kilo")) return "kg";
  return null;
}

function normalizarUndComparavel(nome: string | null | undefined): string {
  return (nome ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function converterMassa(
  valor: number,
  de: UndMassa,
  para: UndMassa
): number | null {
  if (de === para) return valor;
  if (de === "gr" && para === "kg") return valor / 1000;
  if (de === "kg" && para === "gr") return valor * 1000;
  return null;
}

function formatQuantidadeResultado(valor: number): string {
  const arredondado = Math.round(valor * 1000) / 1000;
  const clamped = Math.max(0, arredondado);
  if (Number.isInteger(clamped)) return String(clamped);
  return String(clamped);
}

/** Produção sugerida = base − sobra (somente exibição; não persiste). */
export function computeSugestaoProducao(
  baseValor: string,
  undBaseNome: string | null | undefined,
  sobraValor: string | null | undefined,
  undSobraNome: string | null | undefined
): string {
  const baseNum = parseQuantidade(baseValor);
  if (baseNum == null) return "";

  const sobraTxt = (sobraValor ?? "").trim();
  if (!sobraTxt) return formatQuantidadeResultado(baseNum);

  const sobraNum = parseQuantidade(sobraTxt);
  if (sobraNum == null) return formatQuantidadeResultado(baseNum);

  const undBase = normalizarUndComparavel(undBaseNome);
  const undSobra = normalizarUndComparavel(undSobraNome);

  if (undBase && undSobra && undBase === undSobra) {
    return formatQuantidadeResultado(baseNum - sobraNum);
  }

  const massaBase = normalizarUndMassa(undBaseNome);
  const massaSobra = normalizarUndMassa(undSobraNome);
  if (massaBase && massaSobra) {
    const sobraNaBase = converterMassa(sobraNum, massaSobra, massaBase);
    if (sobraNaBase != null) {
      return formatQuantidadeResultado(baseNum - sobraNaBase);
    }
  }

  return "";
}

/** Sem cronômetro / mín. 1 min quando a sugestão já é 0 (sobra ≥ base). */
export function sugestaoProducaoIsentaCronometro(
  sugestao: string | null | undefined
): boolean {
  const n = parseQuantidade(sugestao);
  return n != null && n <= 0;
}

/** `nao_usa_timer` só vale nas fichas a partir desta data (ISO). */
export const NAO_USA_TIMER_A_PARTIR_DE = "2026-09-03";

export function itemNaoUsaTimerNaFicha(
  naoUsaTimer: boolean | null | undefined,
  dataFichaISO: string | null | undefined
): boolean {
  if (naoUsaTimer !== true) return false;
  const iso = String(dataFichaISO ?? "").trim();
  return iso >= NAO_USA_TIMER_A_PARTIR_DE;
}
